// ─────────────────────────────────────────
// GroupTournament.js — season-long points tournament
// ─────────────────────────────────────────
// Multi-round tournament with configurable points per place.
// Each round has matchups (groups of players competing against each other).
// Standings update automatically as matchup scores are saved.
//
// Views: list → create → detail | enterResults | editSettings

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import AvatarImg from './AvatarImg'
import ScorecardOverlay from './ScorecardOverlay'

const PLACE_FIELDS = ['points_1st', 'points_2nd', 'points_3rd', 'points_4th']
const PLACE_LABELS = ['1st', '2nd', '3rd', '4th']
const DEFAULT_POINTS = { points_1st: 10, points_2nd: 6, points_3rd: 4, points_4th: 2 }
const ROUND_OPTIONS = [2, 3, 4, 5, 6, 8, 10]

const SCORING_FORMATS = [
  { key: 'best_ball', label: 'Best Ball', desc: 'Everyone plays their own ball. Lowest score on each hole counts for the team.' },
  { key: 'best_ball_skins', label: 'Best Ball Skins', desc: 'Each hole is worth a skin. Best ball per team, lowest net score wins it.' },
  { key: 'scramble', label: 'Scramble', desc: 'Everyone hits, pick the best shot, all play from there. Repeat until holed.' },
  { key: 'shamble', label: 'Shamble', desc: "Everyone plays from the best drive, then plays their own ball in. Best score on each hole counts." },
  { key: 'alternate_shot', label: 'Alternate Shot', desc: 'Partners take turns hitting the same ball. One tees off on odds, the other on evens.' },
  { key: 'greensomes', label: 'Greensomes', desc: 'Both partners tee off, pick the better drive, then alternate shots from there.' },
  { key: 'chapman', label: 'Chapman / Pinehurst', desc: 'Both tee off, switch balls for the second shot, then pick one ball and alternate to finish.' },
  { key: 'las_vegas', label: 'Las Vegas', desc: 'Two scores combined into a 2-digit number (low digit first). A 4 and a 6 = 46. Lowest total wins.' },
  { key: 'triple_6_mixer', label: 'Triple 6 Mixer', desc: 'Par 3s: Best Ball. Par 4s: Vegas (2-digit combo). Par 5s: Combined (add both). Lowest total wins.' },
  { key: 'double_dice', label: 'Double Dice', desc: 'Front 9: Combined (add both scores). Back 9: Vegas (2-digit combo, low digit first). Lowest total wins.' },
]

const TEAM_FORMATS = [
  { key: 'individual', label: 'Individual', desc: 'Every player for themselves' },
  { key: 'head_to_head', label: 'Head to Head', desc: '2 players per group — match play, stroke, or Stableford' },
  { key: '2v2_teams', label: '2v2 Teams', desc: 'Pairs compete against each other' },
  { key: 'all_4', label: 'All 4 (Same Team)', desc: 'All 4 players work together' },
]

function calcPoints(position, round) {
  const pos = parseInt(position)
  if (!pos || !round) return 0
  if (pos === 1) return parseInt(round.points_1st) || 0
  if (pos === 2) return parseInt(round.points_2nd) || 0
  if (pos === 3) return parseInt(round.points_3rd) || 0
  if (pos === 4) return parseInt(round.points_4th) || 0
  return 0
}

function formatScheduleDate(dateStr) {
  if (!dateStr) return null
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric'
  })
}

export default function GroupTournament({ groupId, session, members, isMember }) {
  // view: list | create | detail | enterResults | editSettings
  const [view, setView] = useState('list')
  const [tournaments, setTournaments] = useState([])
  const [loading, setLoading] = useState(true)

  // Selected tournament
  const [selectedT, setSelectedT] = useState(null)
  const [tMembers, setTMembers] = useState([])
  const [tRounds, setTRounds] = useState([])
  const [tResults, setTResults] = useState({}) // { round_id: { member_id: result } }

  // Matchup state
  const [matchupsByRound, setMatchupsByRound] = useState({}) // round_id → [{...matchup, entries:[...]}]
  const [addingMatchupToRound, setAddingMatchupToRound] = useState(null) // round_id | null
  const [newMatchupName, setNewMatchupName] = useState('')
  const [newMatchupPlayers, setNewMatchupPlayers] = useState([]) // tournament_member ids
  const [addingMatchup, setAddingMatchup] = useState(false)
  const [scoringMatchup, setScoringMatchup] = useState(null) // { matchup, round }
  const [savingMatchupScores, setSavingMatchupScores] = useState(false)

  // Create form
  const [tName, setTName] = useState('')
  const [numRounds, setNumRounds] = useState(4)
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [guestPlayers, setGuestPlayers] = useState([])
  const [roundConfigs, setRoundConfigs] = useState([])
  const [creating, setCreating] = useState(false)

  // Enter results (manual fallback for rounds without matchups)
  const [selectedRound, setSelectedRound] = useState(null)
  const [resultsInput, setResultsInput] = useState({})
  const [savingResults, setSavingResults] = useState(false)

  // Edit settings
  const [editTName, setEditTName] = useState('')
  const [editRoundConfigs, setEditRoundConfigs] = useState([])
  const [savingEdit, setSavingEdit] = useState(false)

  useEffect(() => { fetchTournaments() }, [groupId])

  // Rebuild round config template whenever numRounds changes
  useEffect(() => {
    setRoundConfigs(Array.from({ length: numRounds }, (_, i) => ({
      round_number: i + 1,
      name: `Round ${i + 1}`,
      scheduled_date: '',
      tee_time: '',
      course_name: '',
      scoring_type: 'rounds',
      scoring_format: 'best_ball',
      team_format: 'individual',
      ...DEFAULT_POINTS
    })))
  }, [numRounds])

  // ── Data fetching ─────────────────────────────────────

  async function fetchTournaments() {
    const { data } = await supabase
      .from('tournaments')
      .select('*')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false })
    setTournaments(data || [])
    setLoading(false)
  }

  async function fetchTournamentDetail(tId) {
    // Rounds + members in parallel
    const [{ data: rounds }, { data: mems }] = await Promise.all([
      supabase.from('tournament_rounds').select('*').eq('tournament_id', tId).order('round_number'),
      supabase.from('tournament_members').select('*').eq('tournament_id', tId).order('total_points', { ascending: false })
    ])

    // Profiles for real users
    const realUserIds = (mems || []).filter(m => m.user_id).map(m => m.user_id)
    let profileMap = {}
    if (realUserIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles').select('id, username, avatar_emoji, avatar_url, handicap').in('id', realUserIds)
      ;(profiles || []).forEach(p => { profileMap[p.id] = p })
    }

    const membersWithProfiles = (mems || []).map(m => ({
      ...m,
      profiles: m.user_id ? (profileMap[m.user_id] || null) : null
    }))

    // Manual results keyed by round_id → member_id
    const roundIds = (rounds || []).map(r => r.id)
    let resultsByRound = {}
    if (roundIds.length > 0) {
      const { data: resultRows } = await supabase
        .from('tournament_results').select('*').in('round_id', roundIds)
      ;(resultRows || []).forEach(r => {
        if (!resultsByRound[r.round_id]) resultsByRound[r.round_id] = {}
        resultsByRound[r.round_id][r.member_id] = r
      })
    }

    // Matchups for all rounds
    let matchupsByRoundObj = {}
    if (roundIds.length > 0) {
      const { data: matchups } = await supabase
        .from('tournament_matchups')
        .select('*')
        .in('round_id', roundIds)
        .order('created_at')

      if (matchups?.length > 0) {
        const mIds = matchups.map(m => m.id)
        const { data: mEntries } = await supabase
          .from('tournament_matchup_entries')
          .select('*')
          .in('matchup_id', mIds)

        // Resolve any entry profiles not yet in profileMap
        const entryUserIds = [...new Set((mEntries || []).filter(e => e.user_id).map(e => e.user_id))]
        const missingIds = entryUserIds.filter(id => !profileMap[id])
        if (missingIds.length > 0) {
          const { data: extraProfiles } = await supabase
            .from('profiles').select('id, username, avatar_emoji, avatar_url, handicap').in('id', missingIds)
          ;(extraProfiles || []).forEach(p => { profileMap[p.id] = p })
        }

        matchups.forEach(m => {
          const entries = (mEntries || [])
            .filter(e => e.matchup_id === m.id)
            .map(e => ({ ...e, profiles: e.user_id ? (profileMap[e.user_id] || null) : null }))
          if (!matchupsByRoundObj[m.round_id]) matchupsByRoundObj[m.round_id] = []
          matchupsByRoundObj[m.round_id].push({ ...m, entries })
        })
      }
    }

    setTRounds(rounds || [])
    setTMembers(membersWithProfiles)
    setTResults(resultsByRound)
    setMatchupsByRound(matchupsByRoundObj)
  }

  // ── Create tournament ─────────────────────────────────

  async function handleCreate() {
    const totalPlayers = selectedPlayers.length + guestPlayers.length
    if (!tName.trim() || totalPlayers < 2) return
    setCreating(true)

    const { data: t, error } = await supabase
      .from('tournaments')
      .insert({ group_id: groupId, name: tName.trim(), num_rounds: numRounds, created_by: session.user.id, status: 'active' })
      .select().single()

    if (error || !t) { setCreating(false); return }

    await supabase.from('tournament_rounds').insert(
      roundConfigs.map(r => ({
        tournament_id: t.id,
        round_number: r.round_number,
        name: r.name || `Round ${r.round_number}`,
        scheduled_date: r.scheduled_date || null,
        tee_time: r.tee_time || null,
        course_name: r.course_name?.trim() || null,
        scoring_type: r.scoring_type || 'rounds',
        scoring_format: r.scoring_format || 'best_ball',
        team_format: r.team_format || 'individual',
        points_1st: parseInt(r.points_1st) || 0,
        points_2nd: parseInt(r.points_2nd) || 0,
        points_3rd: parseInt(r.points_3rd) || 0,
        points_4th: parseInt(r.points_4th) || 0,
        status: 'upcoming'
      }))
    )

    await supabase.from('tournament_members').insert([
      ...selectedPlayers.map(uid => ({ tournament_id: t.id, user_id: uid, guest_name: null, total_points: 0 })),
      ...guestPlayers.map(g => ({ tournament_id: t.id, user_id: null, guest_name: g.name.trim() || 'Guest', total_points: 0 }))
    ])

    setTName(''); setNumRounds(4); setSelectedPlayers([]); setGuestPlayers([])
    setCreating(false)
    await fetchTournaments()
    setSelectedT(t)
    await fetchTournamentDetail(t.id)
    setView('detail')
  }

  function updateRoundConfig(i, field, value) {
    setRoundConfigs(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r))
  }

  // ── Guest helpers ─────────────────────────────────────

  function addGuest() {
    setGuestPlayers(prev => [...prev, { tempId: `g-${Date.now()}`, name: '' }])
  }
  function removeGuest(tempId) {
    setGuestPlayers(prev => prev.filter(g => g.tempId !== tempId))
  }
  function updateGuestName(tempId, name) {
    setGuestPlayers(prev => prev.map(g => g.tempId === tempId ? { ...g, name } : g))
  }

  // ── Enter results (admin score entry) ────────────────

  const [selfReportedScores, setSelfReportedScores] = useState({}) // memberId → score (from LogRound)
  const [markRoundFinished, setMarkRoundFinished] = useState(false)

  async function openEnterResults(round) {
    setSelectedRound(round)
    setMarkRoundFinished(round.status === 'finished')

    // Load existing results from tournament_results
    const existing = tResults[round.id] || {}

    // Also fetch any self-reported scores from matchup entries for this round
    const { data: matchupEntryRows } = await supabase
      .from('tournament_matchup_entries')
      .select('member_id, score')
      .eq('round_id', round.id)
      .not('score', 'is', null)

    const selfReported = {}
    ;(matchupEntryRows || []).forEach(e => {
      if (e.member_id && e.score != null) selfReported[e.member_id] = e.score
    })
    setSelfReportedScores(selfReported)

    const initial = {}
    tMembers.forEach(m => {
      const ex = existing[m.id]
      // Pre-fill: use tournament_results if set, else fall back to self-reported matchup score
      const preScore = ex?.score != null ? String(ex.score)
        : selfReported[m.id] != null ? String(selfReported[m.id]) : ''
      initial[m.id] = {
        position: ex?.position != null ? String(ex.position) : '',
        score: preScore
      }
    })
    setResultsInput(initial)
    setView('enterResults')
  }

  function updateResult(memberId, field, value) {
    setResultsInput(prev => ({ ...prev, [memberId]: { ...(prev[memberId] || {}), [field]: value } }))
  }

  async function handleSaveResults() {
    if (!selectedRound || savingResults) return
    setSavingResults(true)

    // Upsert each member result individually so we don't wipe other data
    for (const m of tMembers) {
      const input = resultsInput[m.id] || {}
      const score = parseInt(input.score) || null
      const pos = parseInt(input.position) || null
      const pts = calcPoints(pos, selectedRound)

      await supabase.from('tournament_results').upsert({
        round_id: selectedRound.id,
        tournament_id: selectedT.id,
        member_id: m.id,
        position: pos,
        score,
        points_earned: pts
      }, { onConflict: 'round_id,member_id' })

      // Also sync score into matchup entries for this member/round
      if (score != null) {
        const { data: matchups } = await supabase
          .from('tournament_matchups')
          .select('id')
          .eq('round_id', selectedRound.id)
        if (matchups?.length > 0) {
          await supabase
            .from('tournament_matchup_entries')
            .update({ score })
            .eq('member_id', m.id)
            .in('matchup_id', matchups.map(mu => mu.id))
        }
      }
    }

    if (markRoundFinished) {
      await supabase.from('tournament_rounds').update({ status: 'finished' }).eq('id', selectedRound.id)
    }

    await recalcTotalPoints()
    setSavingResults(false)
    await fetchTournamentDetail(selectedT.id)
    setView('detail')
  }

  // ── Edit settings ─────────────────────────────────────

  function openEditSettings() {
    setEditTName(selectedT.name)
    setEditRoundConfigs(tRounds.map(r => ({ ...r })))
    setView('editSettings')
  }

  function updateEditRound(i, field, value) {
    setEditRoundConfigs(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r))
  }

  async function handleSaveSettings() {
    setSavingEdit(true)
    if (editTName.trim()) {
      await supabase.from('tournaments').update({ name: editTName.trim() }).eq('id', selectedT.id)
      setSelectedT(prev => ({ ...prev, name: editTName.trim() }))
    }
    for (const r of editRoundConfigs) {
      await supabase.from('tournament_rounds').update({
        name: r.name || `Round ${r.round_number}`,
        scheduled_date: r.scheduled_date || null,
        tee_time: r.tee_time || null,
        course_name: r.course_name?.trim() || null,
        scoring_type: r.scoring_type || 'rounds',
        scoring_format: r.scoring_format || 'best_ball',
        team_format: r.team_format || 'individual',
        points_1st: parseInt(r.points_1st) || 0,
        points_2nd: parseInt(r.points_2nd) || 0,
        points_3rd: parseInt(r.points_3rd) || 0,
        points_4th: parseInt(r.points_4th) || 0,
      }).eq('id', r.id)
    }
    setSavingEdit(false)
    await fetchTournamentDetail(selectedT.id)
    setView('detail')
  }

  // ── Matchup helpers ───────────────────────────────────

  function toggleMatchupPlayer(memberId) {
    setNewMatchupPlayers(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    )
  }

  async function handleAddMatchup() {
    if (!addingMatchupToRound || newMatchupPlayers.length < 2 || addingMatchup) return
    setAddingMatchup(true)

    const { data: matchup } = await supabase
      .from('tournament_matchups')
      .insert({
        tournament_id: selectedT.id,
        round_id: addingMatchupToRound,
        name: newMatchupName.trim() || 'Group',
        status: 'upcoming'
      })
      .select().single()

    if (matchup) {
      const entries = newMatchupPlayers.map(memberId => {
        const member = tMembers.find(m => m.id === memberId)
        return {
          matchup_id: matchup.id,
          tournament_id: selectedT.id,
          round_id: addingMatchupToRound,
          member_id: memberId,
          user_id: member?.user_id || null,
          guest_name: member?.guest_name || null
        }
      })
      await supabase.from('tournament_matchup_entries').insert(entries)
    }

    setAddingMatchupToRound(null)
    setNewMatchupName('')
    setNewMatchupPlayers([])
    setAddingMatchup(false)
    await fetchTournamentDetail(selectedT.id)
  }

  async function handleDeleteMatchup(matchupId) {
    if (!window.confirm('Remove this matchup?')) return
    await supabase.from('tournament_matchups').delete().eq('id', matchupId)
    await recalcTotalPoints()
    await fetchTournamentDetail(selectedT.id)
  }

  function openMatchupScoring(matchup, round) {
    setScoringMatchup({ matchup, round })
  }

  async function handleSaveMatchupScores(results) {
    if (!scoringMatchup || savingMatchupScores) return
    setSavingMatchupScores(true)
    const { matchup } = scoringMatchup

    for (const r of results) {
      await supabase.from('tournament_matchup_entries')
        .update({
          score: r.score,
          hole_scores: r.hole_scores,
          position: r.position,
          points_earned: r.points_earned
        })
        .eq('id', r.id)
    }

    const allHavePositions = results.every(r => r.position != null)
    if (allHavePositions) {
      await supabase.from('tournament_matchups').update({ status: 'finished' }).eq('id', matchup.id)
    }

    await recalcTotalPoints()
    setSavingMatchupScores(false)
    setScoringMatchup(null)
    await fetchTournamentDetail(selectedT.id)
  }

  async function recalcTotalPoints() {
    const [{ data: manualResults }, { data: matchupResults }] = await Promise.all([
      supabase.from('tournament_results').select('member_id, points_earned').eq('tournament_id', selectedT.id),
      supabase.from('tournament_matchup_entries')
        .select('member_id, points_earned')
        .eq('tournament_id', selectedT.id)
        .not('member_id', 'is', null)
    ])

    const totalsMap = {}
    for (const r of [...(manualResults || []), ...(matchupResults || [])]) {
      totalsMap[r.member_id] = (totalsMap[r.member_id] || 0) + (r.points_earned || 0)
    }
    for (const m of tMembers) {
      await supabase.from('tournament_members')
        .update({ total_points: totalsMap[m.id] || 0 })
        .eq('id', m.id)
    }
  }

  // ── General helpers ───────────────────────────────────

  function displayName(m) {
    if (m.user_id) return `@${m.profiles?.username || 'member'}`
    return m.guest_name || 'Guest'
  }

  function entryDisplayName(e) {
    if (e.user_id) return `@${e.profiles?.username || 'member'}`
    return e.guest_name || 'Guest'
  }

  function openDetail(t) {
    setSelectedT(t)
    fetchTournamentDetail(t.id)
    setView('detail')
  }

  function getHoleProgress(entry) {
    const hs = entry.hole_scores
    if (!hs || !Array.isArray(hs)) return null
    const filled = hs.filter(h => h != null)
    if (filled.length === 0) return null
    return {
      holesPlayed: filled.length,
      currentHole: Math.min(filled.length + 1, 18),
      isComplete: filled.length >= 18
    }
  }

  function closeDetail() {
    setView('list')
    setSelectedT(null)
    setTMembers([])
    setTRounds([])
    setTResults({})
    setMatchupsByRound({})
    setScoringMatchup(null)
    setAddingMatchupToRound(null)
  }

  // ═══════════════════════════════════════════════════════
  // RENDER: LIST
  // ═══════════════════════════════════════════════════════

  if (view === 'list') return (
    <div style={s.wrapper}>
      <div style={s.listHeader}>
        <div style={s.listTitle}>Tournaments</div>
        {isMember && <button style={s.createBtn} onClick={() => setView('create')}>＋ New</button>}
      </div>

      {loading && <div style={s.empty}>Loading...</div>}

      {!loading && tournaments.length === 0 && (
        <div style={s.emptyState}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🏅</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1f13', marginBottom: 6 }}>No tournaments yet</div>
          <div style={{ fontSize: 13, color: '#6b7c6e' }}>Create a season-long points tournament for your group.</div>
        </div>
      )}

      <div style={s.tourneyList}>
        {tournaments.map(t => (
          <div key={t.id} style={s.tourneyCard} onClick={() => openDetail(t)}>
            <div style={{
              ...s.tourneyBanner,
              background: t.status === 'active'
                ? 'linear-gradient(135deg,#0e1f13,#1a5e30)'
                : 'linear-gradient(135deg,#2a2a2a,#444)'
            }}>
              <div style={s.tourneyBannerLeft}>
                <div style={s.tourneyStatus}>
                  {t.status === 'active' ? <><span style={s.liveDot} />LIVE</> : <span style={{ opacity: 0.7 }}>FINAL</span>}
                </div>
                <div style={s.tourneyBannerRounds}>{t.num_rounds} Rounds</div>
              </div>
              <div style={s.tourneyBannerDate}>
                {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <div style={s.tourneyCardBody}>
              <div style={s.tourneyName}>{t.name}</div>
              <div style={s.tourneyMeta}>Season points tournament · Tap to view standings →</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // ═══════════════════════════════════════════════════════
  // RENDER: CREATE
  // ═══════════════════════════════════════════════════════

  if (view === 'create') {
    const totalPlayers = selectedPlayers.length + guestPlayers.length
    return (
      <div style={s.wrapper}>
        <div style={s.pageHeader}>
          <button style={s.backBtn} onClick={() => setView('list')}>← Back</button>
          <div style={s.pageTitle}>New Tournament</div>
          <div style={{ width: 60 }} />
        </div>

        <div style={s.formBody}>

          <div style={s.field}>
            <label style={s.label}>Tournament Name</label>
            <input style={s.input} placeholder="e.g. Spring Cup 2026"
              value={tName} onChange={e => setTName(e.target.value)} maxLength={60} />
          </div>

          <div style={s.field}>
            <label style={s.label}>Number of Rounds</label>
            <div style={s.optionGrid}>
              {ROUND_OPTIONS.map(n => (
                <button key={n}
                  style={{ ...s.optionBtn, ...(numRounds === n ? s.optionActive : {}) }}
                  onClick={() => setNumRounds(n)}>{n}</button>
              ))}
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Players <span style={{ color: '#6b7c6e', fontWeight: 400 }}>(select at least 2)</span></label>
            {members.map(m => {
              const profile = m.profiles || {}
              const uid = m.user_id
              const selected = selectedPlayers.includes(uid)
              return (
                <div key={uid} style={s.playerRow}>
                  <div style={{ ...s.playerCheck, background: selected ? '#0e1f13' : 'white', borderColor: selected ? '#0e1f13' : '#e2ebe4' }}
                    onClick={() => setSelectedPlayers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid])}>
                    {selected && <span style={{ color: '#c8f135', fontSize: 12 }}>✓</span>}
                  </div>
                  <div style={s.playerAvatar}><AvatarImg profile={profile} /></div>
                  <div style={s.playerInfo}>
                    <div style={s.playerName}>@{profile.username || 'member'}</div>
                    {profile.handicap != null && <div style={s.playerHcp}>HCP {profile.handicap}</div>}
                  </div>
                </div>
              )
            })}

            {guestPlayers.length > 0 && (
              <div style={s.guestDivider}><span style={s.guestDividerLabel}>Guests</span></div>
            )}
            {guestPlayers.map(g => (
              <div key={g.tempId} style={s.guestRow}>
                <div style={s.guestAvatar}>👤</div>
                <input style={s.guestNameInput} placeholder="Guest name"
                  value={g.name} onChange={e => updateGuestName(g.tempId, e.target.value)} maxLength={40} />
                <button style={s.guestRemoveBtn} onClick={() => removeGuest(g.tempId)}>✕</button>
              </div>
            ))}
            <button style={s.addGuestBtn} onClick={addGuest}>＋ Add Guest Player</button>
          </div>

          <div style={s.field}>
            <label style={s.label}>Round Settings</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {roundConfigs.map((r, i) => (
                <div key={i} style={s.roundConfigCard}>
                  <div style={s.roundConfigHeader}>
                    <div style={s.roundConfigNum}>R{r.round_number}</div>
                    <input style={s.roundNameInput} placeholder={`Round ${r.round_number} name`}
                      value={r.name} onChange={e => updateRoundConfig(i, 'name', e.target.value)} maxLength={40} />
                  </div>
                  <div style={s.roundConfigRow}>
                    <div style={{ flex: 1 }}>
                      <div style={s.miniLabel}>Date (optional)</div>
                      <input style={s.miniInput} type="date"
                        value={r.scheduled_date} onChange={e => updateRoundConfig(i, 'scheduled_date', e.target.value)} />
                    </div>
                    <div style={{ width: 120 }}>
                      <div style={s.miniLabel}>Tee Time</div>
                      <input style={s.miniInput} placeholder="e.g. 8:00am"
                        value={r.tee_time} onChange={e => updateRoundConfig(i, 'tee_time', e.target.value)} maxLength={20} />
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={s.miniLabel}>Course (optional)</div>
                    <input style={s.miniInput} placeholder="e.g. Augusta National"
                      value={r.course_name || ''} onChange={e => updateRoundConfig(i, 'course_name', e.target.value)} maxLength={80} />
                  </div>
                  <div style={s.scoringTypeToggleRow}>
                    <div style={s.miniLabel}>Scoring</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      {[{ key: 'rounds', label: '⛳ Golf Score', hint: 'low wins' }, { key: 'custom', label: '🎯 Custom Points', hint: 'high wins' }].map(opt => (
                        <button key={opt.key}
                          style={{ ...s.scoringTypeBtn, ...(r.scoring_type === opt.key ? s.scoringTypeBtnActive : {}) }}
                          onClick={() => updateRoundConfig(i, 'scoring_type', opt.key)}>
                          <span>{opt.label}</span>
                          <span style={{ fontSize: 9, opacity: 0.65, marginLeft: 4 }}>({opt.hint})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={s.miniLabel}>Team Format</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                      {TEAM_FORMATS.map(f => (
                        <div key={f.key} style={{ ...s.formatCard, ...(r.team_format === f.key ? s.formatCardActive : {}) }}
                          onClick={() => updateRoundConfig(i, 'team_format', f.key)}>
                          <div style={{ flex: 1 }}>
                            <div style={s.formatCardTitle}>{f.label}</div>
                            <div style={s.formatCardDesc}>{f.desc}</div>
                          </div>
                          {r.team_format === f.key && <div style={s.formatDot} />}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={s.miniLabel}>Scoring Format</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                      {SCORING_FORMATS.map(f => (
                        <div key={f.key} style={{ ...s.formatCard, ...(r.scoring_format === f.key ? s.formatCardActive : {}) }}
                          onClick={() => updateRoundConfig(i, 'scoring_format', f.key)}>
                          <div style={{ flex: 1 }}>
                            <div style={s.formatCardTitle}>{f.label}</div>
                            <div style={s.formatCardDesc}>{f.desc}</div>
                          </div>
                          {r.scoring_format === f.key && <div style={s.formatDot} />}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={s.pointsRow}>
                    {PLACE_LABELS.map((place, pi) => (
                      <div key={place} style={s.pointsCell}>
                        <div style={s.pointsPlaceLabel}>{place}</div>
                        <input style={s.pointsInput} type="number" min={0} max={999}
                          value={r[PLACE_FIELDS[pi]]}
                          onChange={e => updateRoundConfig(i, PLACE_FIELDS[pi], e.target.value)} />
                        <div style={s.pointsPts}>pts</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            style={{ ...s.submitBtn, opacity: tName.trim() && totalPlayers >= 2 && !creating ? 1 : 0.4 }}
            onClick={handleCreate}
            disabled={!tName.trim() || totalPlayers < 2 || creating}>
            {creating ? 'Creating...' : '🏅 Create Tournament'}
          </button>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════
  // RENDER: DETAIL — Standings + Schedule + Matchups
  // ═══════════════════════════════════════════════════════

  if (view === 'detail' && selectedT) {
    const isCreator = session.user.id === selectedT.created_by
    const isActive = selectedT.status === 'active'
    const sortedMembers = [...tMembers].sort((a, b) => b.total_points - a.total_points)
    const roundsCompleted = tRounds.filter(r => r.status === 'finished').length

    return (
      <div style={s.wrapper}>

        {/* ── Matchup scoring — multi-player scorecard ── */}
        {scoringMatchup && (
          <ScorecardOverlay
            entries={scoringMatchup.matchup.entries}
            onSave={handleSaveMatchupScores}
            onClose={() => setScoringMatchup(null)}
            title={scoringMatchup.matchup.name}
            showPositions={true}
            round={scoringMatchup.round}
          />
        )}

        {/* ── Add matchup modal ── */}
        {addingMatchupToRound && (
          <div style={s.modalOverlay}>
            <div style={s.modal}>
              <div style={s.modalHeader}>
                <button style={s.modalCloseBtn} onClick={() => {
                  setAddingMatchupToRound(null); setNewMatchupPlayers([]); setNewMatchupName('')
                }}>✕</button>
                <div style={s.modalTitle}>Add Matchup</div>
                <button
                  style={{ ...s.saveHeaderBtn, opacity: newMatchupPlayers.length >= 2 && !addingMatchup ? 1 : 0.4 }}
                  onClick={handleAddMatchup}
                  disabled={newMatchupPlayers.length < 2 || addingMatchup}>
                  {addingMatchup ? '...' : 'Add'}
                </button>
              </div>

              <div style={{ padding: '16px', overflowY: 'auto', maxHeight: 'calc(70vh - 60px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={s.field}>
                  <label style={s.label}>Matchup Name</label>
                  <input style={s.input} placeholder="e.g. Group A, Morning Wave"
                    value={newMatchupName}
                    onChange={e => setNewMatchupName(e.target.value)} maxLength={40} />
                </div>

                <div style={s.field}>
                  <label style={s.label}>Players <span style={{ color: '#6b7c6e', fontWeight: 400 }}>(select at least 2)</span></label>
                  {tMembers.map(m => {
                    const selected = newMatchupPlayers.includes(m.id)
                    return (
                      <div key={m.id} style={s.playerRow}>
                        <div
                          style={{ ...s.playerCheck, background: selected ? '#0e1f13' : 'white', borderColor: selected ? '#0e1f13' : '#e2ebe4' }}
                          onClick={() => toggleMatchupPlayer(m.id)}>
                          {selected && <span style={{ color: '#c8f135', fontSize: 12 }}>✓</span>}
                        </div>
                        <div style={s.playerAvatar}>{m.user_id ? <AvatarImg profile={m.profiles} /> : '👤'}</div>
                        <div style={s.playerInfo}>
                          <div style={s.playerName}>{displayName(m)}</div>
                          {!m.user_id && <div style={s.playerHcp}>Guest</div>}
                          {m.profiles?.handicap != null && <div style={s.playerHcp}>HCP {m.profiles.handicap}</div>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ESPN-style tournament header ── */}
        <div style={s.detailBanner}>
          <button style={s.backBtnLight} onClick={closeDetail}>← Back</button>
          <div style={s.detailBannerCenter}>
            <div style={s.detailBannerTitle}>{selectedT.name}</div>
            <div style={s.detailBannerMeta}>
              {roundsCompleted} of {selectedT.num_rounds} rounds complete
            </div>
          </div>
          {isCreator ? (
            <button style={s.editSettingsBtn} onClick={openEditSettings}>⚙ Edit</button>
          ) : (
            <div style={{
              ...s.detailStatusBadge,
              background: isActive ? '#c8f135' : 'rgba(255,255,255,0.15)',
              color: isActive ? '#0e1f13' : 'rgba(255,255,255,0.6)'
            }}>
              {isActive ? '● LIVE' : '✓ DONE'}
            </div>
          )}
        </div>

        {/* ── ESPN Golf Leaderboard ── */}
        <div style={s.lbSection}>
          <div style={s.lbSectionHeader}>
            <div style={s.lbSectionTitle}>LEADERBOARD</div>
            <div style={s.lbSectionSub}>
              Points: {tRounds[0] ? `${tRounds[0].points_1st}/${tRounds[0].points_2nd}/${tRounds[0].points_3rd}/${tRounds[0].points_4th}` : '—'} per place
            </div>
          </div>

          <div style={s.lbTableWrap}>
            <div style={{ ...s.lbRow, ...s.lbHeaderRow, minWidth: lbMinWidth(tRounds.length) }}>
              <div style={s.lbColRank}>#</div>
              <div style={s.lbColPlayer}>PLAYER</div>
              {tRounds.map(r => (
                <div key={r.id} style={s.lbColRound}>R{r.round_number}</div>
              ))}
              <div style={s.lbColTotal}>PTS</div>
            </div>

            {sortedMembers.length === 0 && (
              <div style={s.empty}>No players in this tournament yet.</div>
            )}

            {sortedMembers.map((m, i) => {
              const isLeader = i === 0 && m.total_points > 0
              const medals = ['🥇', '🥈', '🥉']
              return (
                <div
                  key={m.id}
                  style={{
                    ...s.lbRow,
                    minWidth: lbMinWidth(tRounds.length),
                    background: isLeader ? 'rgba(200,241,53,0.07)' : i % 2 === 0 ? 'white' : '#fafcfb',
                    borderLeft: isLeader ? '3px solid #c8f135' : '3px solid transparent'
                  }}
                >
                  <div style={s.lbColRank}>
                    {i < 3 && m.total_points > 0
                      ? <span style={{ fontSize: 16 }}>{medals[i]}</span>
                      : <span style={s.lbRankNum}>{i + 1}</span>}
                  </div>

                  <div style={s.lbColPlayer}>
                    <div style={s.lbAvatar}>
                      {m.user_id ? <AvatarImg profile={m.profiles} /> : '👤'}
                    </div>
                    <div>
                      <div style={s.lbName}>{displayName(m)}</div>
                      {!m.user_id && <div style={s.lbGuestTag}>Guest</div>}
                      {m.user_id && m.profiles?.handicap != null && (
                        <div style={s.lbHcp}>HCP {m.profiles.handicap}</div>
                      )}
                    </div>
                  </div>

                  {tRounds.map(r => {
                    const manualResult = tResults[r.id]?.[m.id]
                    const manualPts = manualResult?.points_earned || 0
                    const roundMatchups = matchupsByRound[r.id] || []
                    const matchupEntry = roundMatchups.flatMap(mu => mu.entries).find(e => e.member_id === m.id)
                    const matchupPts = roundMatchups.reduce((sum, mu) => {
                      const entry = mu.entries.find(e => e.member_id === m.id)
                      return sum + (entry?.points_earned || 0)
                    }, 0)
                    const totalRoundPts = manualPts + matchupPts
                    const score = manualResult?.score ?? matchupEntry?.score ?? null
                    const hasAnyResult = !!manualResult || !!matchupEntry

                    return (
                      <div key={r.id} style={{ ...s.lbColRound, flexDirection: 'column', gap: 1 }}>
                        <div style={{
                          fontWeight: 800,
                          color: totalRoundPts > 0 ? '#1a7a3c' : hasAnyResult ? '#6b7c6e' : '#9aaa9d'
                        }}>
                          {hasAnyResult ? (totalRoundPts > 0 ? `+${totalRoundPts}` : '0') : '—'}
                        </div>
                        {score != null && (
                          <div style={{ fontSize: 9, color: '#9aaa9d', fontWeight: 600 }}>{score}</div>
                        )}
                      </div>
                    )
                  })}

                  <div style={s.lbColTotal}>
                    <div style={s.lbTotalNum}>{m.total_points}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Schedule & Matchups ── */}
        <div style={s.scheduleSection}>
          <div style={s.scheduleSectionTitle}>SCHEDULE & MATCHUPS</div>

          {tRounds.map(r => {
            const roundMatchups = matchupsByRound[r.id] || []
            const hasManualResults = tResults[r.id] && Object.keys(tResults[r.id]).length > 0
            const canEditAnyMatchup = mu => isCreator || mu.entries.some(e => e.user_id === session.user.id)

            return (
              <div key={r.id} style={s.scheduleCard}>

                {/* Round header */}
                <div style={s.scheduleRoundRow}>
                  <div style={s.scheduleLeft}>
                    <div style={{
                      ...s.scheduleRoundBadge,
                      background: r.status === 'finished' ? '#0e1f13' : r.status === 'active' ? '#1a7a3c' : '#f4f7f5',
                      color: r.status === 'upcoming' ? '#6b7c6e' : 'white'
                    }}>R{r.round_number}</div>
                    <div>
                      <div style={s.scheduleName}>{r.name}</div>
                      {r.scheduled_date && (
                        <div style={s.scheduleDateTime}>
                          {formatScheduleDate(r.scheduled_date)}
                          {r.tee_time && <span> · Tee {r.tee_time}</span>}
                        </div>
                      )}
                      {r.course_name && (
                        <div style={s.scheduleDateTime}>⛳ {r.course_name}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{
                          ...s.scheduleStatusBadge,
                          background: (r.scoring_type || 'rounds') === 'rounds' ? '#e8f5ed' : '#fef3e2',
                          color: (r.scoring_type || 'rounds') === 'rounds' ? '#1a7a3c' : '#c8a000',
                          padding: '2px 8px', fontSize: 10
                        }}>
                          {(r.scoring_type || 'rounds') === 'rounds' ? '⛳ Golf Score' : '🎯 Custom Points'}
                        </span>
                        {r.scoring_format && (
                          <span style={s.formatPill}>
                            {SCORING_FORMATS.find(f => f.key === r.scoring_format)?.label || r.scoring_format}
                          </span>
                        )}
                        {r.team_format && (
                          <span style={s.formatPill}>
                            {TEAM_FORMATS.find(f => f.key === r.team_format)?.label || r.team_format}
                          </span>
                        )}
                      </div>
                      <div style={s.schedulePoints}>
                        {PLACE_LABELS.map((pl, pi) => (
                          <span key={pl} style={s.schedulePointsPill}>
                            {pl}: {r[PLACE_FIELDS[pi]]}pts
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={s.scheduleRight}>
                    <div style={{
                      ...s.scheduleStatusBadge,
                      background: r.status === 'finished' ? '#e8f5ed' : r.status === 'active' ? '#fff3cd' : '#f4f7f5',
                      color: r.status === 'finished' ? '#1a7a3c' : r.status === 'active' ? '#c8a000' : '#6b7c6e'
                    }}>
                      {r.status === 'finished' ? '✓ Done' : r.status === 'active' ? '● Live' : 'Upcoming'}
                    </div>
                    {isCreator && (
                      <button style={s.enterResultsBtn} onClick={() => openEnterResults(r)}>
                        {hasManualResults ? 'Edit Scores' : 'Add Scores'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Matchup list */}
                {roundMatchups.length > 0 && (
                  <div style={s.matchupsList}>
                    {roundMatchups.map(mu => (
                      <div key={mu.id} style={s.matchupCard}>
                        <div style={s.matchupCardHeader}>
                          <div style={s.matchupName}>{mu.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                              ...s.matchupStatusBadge,
                              background: mu.status === 'finished' ? '#e8f5ed' : '#fff8e1',
                              color: mu.status === 'finished' ? '#1a7a3c' : '#c8a000'
                            }}>
                              {mu.status === 'finished' ? '✓ Done' : '● Playing'}
                            </div>
                            {canEditAnyMatchup(mu) && (
                              <button style={s.scoreMatchupBtn} onClick={() => openMatchupScoring(mu, r)}>
                                {mu.status === 'finished' ? 'Edit' : 'Score'}
                              </button>
                            )}
                            {isCreator && (
                              <button style={s.deleteMatchupBtn} onClick={() => handleDeleteMatchup(mu.id)}>✕</button>
                            )}
                          </div>
                        </div>

                        <div style={s.matchupEntries}>
                          {[...mu.entries]
                            .sort((a, b) => (a.position || 99) - (b.position || 99))
                            .map(entry => {
                              const rankIcons = ['🥇', '🥈', '🥉', '🏅']
                              return (
                                <div key={entry.id} style={s.matchupEntryRow}>
                                  <div style={s.matchupEntryRank}>
                                    {entry.position
                                      ? (rankIcons[entry.position - 1] || <span style={s.matchupEntryRankNum}>{entry.position}</span>)
                                      : <span style={{ color: '#c8d5ca', fontSize: 11 }}>—</span>}
                                  </div>
                                  <div style={s.matchupEntryAvatar}>
                                    {entry.user_id ? <AvatarImg profile={entry.profiles} /> : '👤'}
                                  </div>
                                  <div style={s.matchupEntryName}>{entryDisplayName(entry)}</div>
                                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {(() => {
                                      const p = getHoleProgress(entry)
                                      return p && !p.isComplete
                                        ? <div style={s.matchupEntryHole}>● H{p.currentHole}</div>
                                        : null
                                    })()}
                                    {entry.score != null && (
                                      <div style={s.matchupEntryScore}>{entry.score}</div>
                                    )}
                                    {entry.points_earned > 0 && (
                                      <div style={s.matchupEntryPts}>+{entry.points_earned}pts</div>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add matchup button */}
                {isCreator && (
                  <button
                    style={s.addMatchupBtn}
                    onClick={() => {
                      setAddingMatchupToRound(r.id)
                      setNewMatchupName(`Group ${String.fromCharCode(65 + roundMatchups.length)}`)
                      setNewMatchupPlayers([])
                    }}>
                    ＋ Add Matchup
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* Mark tournament finished */}
        {isCreator && isActive && roundsCompleted === tRounds.length && tRounds.length > 0 && (
          <div style={{ padding: '0 16px 24px' }}>
            <button style={s.finishBtn} onClick={async () => {
              if (!window.confirm('Mark this tournament as finished?')) return
              await supabase.from('tournaments').update({ status: 'finished' }).eq('id', selectedT.id)
              setSelectedT(prev => ({ ...prev, status: 'finished' }))
              fetchTournaments()
            }}>
              Mark Tournament as Finished
            </button>
          </div>
        )}
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════
  // RENDER: ENTER RESULTS (manual fallback)
  // ═══════════════════════════════════════════════════════

  if (view === 'enterResults' && selectedRound) {
    return (
      <div style={s.wrapper}>
        <div style={s.pageHeader}>
          <button style={s.backBtn} onClick={() => setView('detail')}>← Back</button>
          <div style={s.pageTitle}>{selectedRound.name}</div>
          <button
            style={{ ...s.saveHeaderBtn, opacity: savingResults ? 0.5 : 1 }}
            onClick={handleSaveResults}
            disabled={savingResults}>
            {savingResults ? '...' : 'Save'}
          </button>
        </div>

        <div style={s.pointsReminder}>
          {PLACE_LABELS.map((pl, pi) => (
            <div key={pl} style={s.pointsReminderCell}>
              <div style={s.pointsReminderNum}>{selectedRound[PLACE_FIELDS[pi]]}</div>
              <div style={s.pointsReminderLabel}>{pl}</div>
            </div>
          ))}
        </div>

        <div style={s.resultsBody}>
          <div style={s.resultsHeaderRow}>
            <div style={{ flex: 1, fontSize: 10, fontWeight: 700, color: '#6b7c6e', letterSpacing: 1, textTransform: 'uppercase' }}>Player</div>
            <div style={s.resultsColLabel}>Place</div>
            <div style={s.resultsColLabel}>Score</div>
            <div style={s.resultsColLabel}>Pts</div>
          </div>

          {tMembers.map(m => {
            const input = resultsInput[m.id] || {}
            const pts = calcPoints(input.position, selectedRound)
            const wasSelfReported = selfReportedScores[m.id] != null
            return (
              <div key={m.id} style={s.resultRow}>
                <div style={s.resultPlayerCell}>
                  <div style={s.resultAvatar}>{m.user_id ? <AvatarImg profile={m.profiles} /> : '👤'}</div>
                  <div>
                    <div style={s.resultName}>{displayName(m)}</div>
                    {wasSelfReported && <div style={s.selfReportedTag}>✓ self-reported</div>}
                    {!m.user_id && <div style={s.resultGuestTag}>Guest</div>}
                  </div>
                </div>
                <div style={s.resultInputCell}>
                  <input
                    style={s.resultPosInput}
                    type="number" min={1} max={tMembers.length}
                    placeholder="—"
                    value={input.position}
                    onChange={e => updateResult(m.id, 'position', e.target.value)}
                  />
                </div>
                <div style={s.resultInputCell}>
                  <input
                    style={s.resultScoreInput}
                    type="number" min={50} max={200}
                    placeholder="—"
                    value={input.score}
                    onChange={e => updateResult(m.id, 'score', e.target.value)}
                  />
                </div>
                <div style={s.resultPtsCell}>
                  <div style={{
                    ...s.resultPtsBadge,
                    background: pts > 0 ? '#0e1f13' : '#f4f7f5',
                    color: pts > 0 ? '#c8f135' : '#9aaa9d'
                  }}>
                    {pts > 0 ? `+${pts}` : '—'}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div style={{ padding: '12px 16px 0' }}>
          <label style={s.finishToggle}>
            <input
              type="checkbox"
              checked={markRoundFinished}
              onChange={e => setMarkRoundFinished(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Mark round as finished
          </label>
        </div>

        <div style={{ padding: '12px 16px 24px' }}>
          <button
            style={{ ...s.submitBtn, opacity: savingResults ? 0.5 : 1 }}
            onClick={handleSaveResults} disabled={savingResults}>
            {savingResults ? 'Saving...' : '✓ Save Scores'}
          </button>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════
  // RENDER: EDIT SETTINGS
  // ═══════════════════════════════════════════════════════

  if (view === 'editSettings') return (
    <div style={s.wrapper}>
      <div style={s.pageHeader}>
        <button style={s.backBtn} onClick={() => setView('detail')}>← Back</button>
        <div style={s.pageTitle}>Edit Settings</div>
        <button
          style={{ ...s.saveHeaderBtn, opacity: savingEdit ? 0.5 : 1 }}
          onClick={handleSaveSettings} disabled={savingEdit}>
          {savingEdit ? '...' : 'Save'}
        </button>
      </div>

      <div style={s.formBody}>

        <div style={s.field}>
          <label style={s.label}>Tournament Name</label>
          <input style={s.input} value={editTName}
            onChange={e => setEditTName(e.target.value)} maxLength={60} />
        </div>

        <div style={s.field}>
          <label style={s.label}>Round Settings</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {editRoundConfigs.map((r, i) => (
              <div key={r.id} style={s.roundConfigCard}>
                <div style={s.roundConfigHeader}>
                  <div style={s.roundConfigNum}>R{r.round_number}</div>
                  <input style={s.roundNameInput} placeholder={`Round ${r.round_number} name`}
                    value={r.name || ''} onChange={e => updateEditRound(i, 'name', e.target.value)} maxLength={40} />
                  <div style={{
                    ...s.scheduleStatusBadge,
                    background: r.status === 'finished' ? '#e8f5ed' : '#f4f7f5',
                    color: r.status === 'finished' ? '#1a7a3c' : '#6b7c6e',
                    marginLeft: 'auto', flexShrink: 0
                  }}>
                    {r.status === 'finished' ? '✓ Done' : 'Upcoming'}
                  </div>
                </div>
                <div style={s.roundConfigRow}>
                  <div style={{ flex: 1 }}>
                    <div style={s.miniLabel}>Date (optional)</div>
                    <input style={s.miniInput} type="date"
                      value={r.scheduled_date || ''} onChange={e => updateEditRound(i, 'scheduled_date', e.target.value)} />
                  </div>
                  <div style={{ width: 120 }}>
                    <div style={s.miniLabel}>Tee Time</div>
                    <input style={s.miniInput} placeholder="e.g. 8:00am"
                      value={r.tee_time || ''} onChange={e => updateEditRound(i, 'tee_time', e.target.value)} maxLength={20} />
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={s.miniLabel}>Course (optional)</div>
                  <input style={s.miniInput} placeholder="e.g. Augusta National"
                    value={r.course_name || ''} onChange={e => updateEditRound(i, 'course_name', e.target.value)} maxLength={80} />
                </div>
                <div style={s.scoringTypeToggleRow}>
                  <div style={s.miniLabel}>Scoring</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    {[{ key: 'rounds', label: '⛳ Golf Score', hint: 'low wins' }, { key: 'custom', label: '🎯 Custom Points', hint: 'high wins' }].map(opt => (
                      <button key={opt.key}
                        style={{ ...s.scoringTypeBtn, ...(( r.scoring_type || 'rounds') === opt.key ? s.scoringTypeBtnActive : {}) }}
                        onClick={() => updateEditRound(i, 'scoring_type', opt.key)}>
                        <span>{opt.label}</span>
                        <span style={{ fontSize: 9, opacity: 0.65, marginLeft: 4 }}>({opt.hint})</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={s.miniLabel}>Team Format</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                    {TEAM_FORMATS.map(f => (
                      <div key={f.key} style={{ ...s.formatCard, ...((r.team_format || 'individual') === f.key ? s.formatCardActive : {}) }}
                        onClick={() => updateEditRound(i, 'team_format', f.key)}>
                        <div style={{ flex: 1 }}>
                          <div style={s.formatCardTitle}>{f.label}</div>
                          <div style={s.formatCardDesc}>{f.desc}</div>
                        </div>
                        {(r.team_format || 'individual') === f.key && <div style={s.formatDot} />}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={s.miniLabel}>Scoring Format</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                    {SCORING_FORMATS.map(f => (
                      <div key={f.key} style={{ ...s.formatCard, ...((r.scoring_format || 'best_ball') === f.key ? s.formatCardActive : {}) }}
                        onClick={() => updateEditRound(i, 'scoring_format', f.key)}>
                        <div style={{ flex: 1 }}>
                          <div style={s.formatCardTitle}>{f.label}</div>
                          <div style={s.formatCardDesc}>{f.desc}</div>
                        </div>
                        {(r.scoring_format || 'best_ball') === f.key && <div style={s.formatDot} />}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={s.pointsRow}>
                  {PLACE_LABELS.map((place, pi) => (
                    <div key={place} style={s.pointsCell}>
                      <div style={s.pointsPlaceLabel}>{place}</div>
                      <input style={s.pointsInput} type="number" min={0} max={999}
                        value={r[PLACE_FIELDS[pi]] ?? ''}
                        onChange={e => updateEditRound(i, PLACE_FIELDS[pi], e.target.value)} />
                      <div style={s.pointsPts}>pts</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          style={{ ...s.submitBtn, opacity: savingEdit ? 0.5 : 1 }}
          onClick={handleSaveSettings} disabled={savingEdit}>
          {savingEdit ? 'Saving...' : '✓ Save Changes'}
        </button>
      </div>
    </div>
  )

  return null
}

// ── Min width for leaderboard table ──
function lbMinWidth(numRounds) {
  return 44 + 140 + (numRounds * 44) + 64
}

// ── STYLES ──────────────────────────────────────────────────────
const s = {
  wrapper: { background: '#f4f7f5', flex: 1, overflowY: 'auto', minHeight: 0 },
  empty: { padding: 24, textAlign: 'center', color: '#6b7c6e', fontSize: 13 },

  // ── List ──
  listHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 16px 12px', background: 'white', borderBottom: '1px solid #f0f4f1'
  },
  listTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: '#0e1f13', letterSpacing: 0.5
  },
  createBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 8, padding: '7px 14px', fontWeight: 800,
    fontSize: 13, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif"
  },
  emptyState: { padding: '48px 20px', textAlign: 'center' },
  tourneyList: { display: 'flex', flexDirection: 'column', gap: 12, padding: 16 },
  tourneyCard: {
    background: 'white', borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.04)'
  },
  tourneyBanner: {
    padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  tourneyBannerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  tourneyStatus: {
    color: 'white', fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
    textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5,
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  liveDot: { display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#c8f135' },
  tourneyBannerRounds: {
    background: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: 20,
    padding: '3px 10px', fontSize: 11, fontWeight: 700,
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  tourneyBannerDate: { color: 'rgba(255,255,255,0.5)', fontSize: 10 },
  tourneyCardBody: { padding: '14px 16px 16px' },
  tourneyName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 20, color: '#0e1f13', marginBottom: 4
  },
  tourneyMeta: { fontSize: 12, color: '#6b7c6e' },

  // ── Page header ──
  pageHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', background: 'white', borderBottom: '1px solid #f0f4f1',
    position: 'sticky', top: 0, zIndex: 5
  },
  pageTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 16, color: '#0e1f13'
  },
  backBtn: {
    background: 'none', border: 'none', color: '#1a7a3c',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", padding: 0
  },
  saveHeaderBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 8, padding: '7px 16px', fontWeight: 800,
    fontSize: 13, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif"
  },

  // ── Create / Edit form ──
  formBody: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 24 },
  field: {},
  label: {
    display: 'block', fontSize: 10, fontWeight: 700,
    letterSpacing: 1.5, textTransform: 'uppercase', color: '#6b7c6e', marginBottom: 8
  },
  input: {
    width: '100%', background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 10, padding: '12px 14px', fontSize: 14,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  },
  optionGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  optionBtn: {
    background: 'white', border: '1.5px solid #e2ebe4', borderRadius: 10,
    padding: '9px 16px', fontSize: 13, fontWeight: 700, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  optionActive: { background: '#e8f5ed', borderColor: '#2aad56', color: '#1a7a3c' },

  // Players
  playerRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0', borderBottom: '1px solid #f0f4f1'
  },
  playerCheck: {
    width: 22, height: 22, borderRadius: 6, border: '1.5px solid #e2ebe4',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0
  },
  playerAvatar: {
    width: 34, height: 34, borderRadius: '50%', fontSize: 18, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  playerInfo: { flex: 1 },
  playerName: { fontWeight: 700, fontSize: 13, color: '#0e1f13' },
  playerHcp: { fontSize: 11, color: '#6b7c6e' },
  guestDivider: { display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0 4px' },
  guestDividerLabel: { fontSize: 10, fontWeight: 700, color: '#6b7c6e', letterSpacing: 1, textTransform: 'uppercase' },
  guestRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f4f1' },
  guestAvatar: {
    width: 34, height: 34, borderRadius: '50%', fontSize: 18, flexShrink: 0,
    background: '#f0f4f1', border: '1.5px dashed #c8d5ca',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  guestNameInput: {
    flex: 1, background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 8, padding: '7px 10px', fontSize: 13,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif", fontWeight: 600
  },
  guestRemoveBtn: {
    background: 'none', border: 'none', color: '#c0392b',
    fontSize: 14, cursor: 'pointer', padding: '4px 6px', flexShrink: 0
  },
  addGuestBtn: {
    marginTop: 10, background: 'none', border: '1.5px dashed #c8d5ca',
    borderRadius: 10, padding: '10px 0', width: '100%',
    fontSize: 13, fontWeight: 700, color: '#6b7c6e', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", letterSpacing: 0.3
  },

  // Round config card
  roundConfigCard: {
    background: 'white', borderRadius: 14, padding: '14px',
    border: '1.5px solid #e2ebe4', display: 'flex', flexDirection: 'column', gap: 10
  },
  roundConfigHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  roundConfigNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 16, color: 'white',
    background: '#0e1f13', borderRadius: 8, padding: '4px 10px', flexShrink: 0
  },
  roundNameInput: {
    flex: 1, background: '#f4f7f5', border: '1px solid #e2ebe4',
    borderRadius: 8, padding: '7px 10px', fontSize: 13,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif", fontWeight: 600
  },
  roundConfigRow: { display: 'flex', gap: 10 },
  miniLabel: { fontSize: 9, fontWeight: 700, color: '#9aaa9d', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 },
  scoringTypeToggleRow: { marginBottom: 8 },
  scoringTypeBtn: {
    flex: 1, background: '#f4f7f5', border: '1.5px solid #e2ebe4', borderRadius: 8,
    padding: '6px 10px', fontSize: 11, fontWeight: 600, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif", display: 'flex', alignItems: 'center'
  },
  scoringTypeBtnActive: { background: '#e8f5ed', borderColor: '#2aad56', color: '#1a7a3c' },
  miniInput: {
    width: '100%', background: '#f4f7f5', border: '1px solid #e2ebe4',
    borderRadius: 8, padding: '7px 10px', fontSize: 13, color: '#0e1f13',
    outline: 'none', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  },
  pointsRow: { display: 'flex', gap: 8 },
  pointsCell: { flex: 1, textAlign: 'center' },
  pointsPlaceLabel: { fontSize: 9, fontWeight: 800, color: '#6b7c6e', letterSpacing: 0.5, marginBottom: 4, textTransform: 'uppercase' },
  pointsInput: {
    width: '100%', background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 8, padding: '8px 4px', fontSize: 16, fontWeight: 900,
    color: '#0e1f13', outline: 'none', textAlign: 'center',
    fontFamily: "'Barlow Condensed', sans-serif", boxSizing: 'border-box',
    MozAppearance: 'textfield'
  },
  pointsPts: { fontSize: 9, color: '#9aaa9d', marginTop: 3 },
  submitBtn: {
    width: '100%', background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },

  // ── Detail: ESPN banner ──
  detailBanner: {
    background: '#0e1f13', padding: '14px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    position: 'sticky', top: 0, zIndex: 5
  },
  backBtnLight: {
    background: 'none', border: 'none', color: '#c8f135',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", padding: 0, flexShrink: 0
  },
  detailBannerCenter: { flex: 1, textAlign: 'center' },
  detailBannerTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: 'white', letterSpacing: 0.5
  },
  detailBannerMeta: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  detailStatusBadge: {
    borderRadius: 20, padding: '4px 10px', fontSize: 10, fontWeight: 800,
    letterSpacing: 1, fontFamily: "'Barlow Condensed', sans-serif", flexShrink: 0
  },
  editSettingsBtn: {
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
    color: 'white', borderRadius: 8, padding: '6px 12px',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", flexShrink: 0
  },

  // ── ESPN Leaderboard ──
  lbSection: { background: 'white', margin: '0 0 12px' },
  lbSectionHeader: { padding: '12px 16px 10px', borderBottom: '1px solid #f0f4f1' },
  lbSectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 13, color: '#0e1f13', letterSpacing: 2
  },
  lbSectionSub: { fontSize: 10, color: '#9aaa9d', marginTop: 2 },
  lbTableWrap: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  lbRow: {
    display: 'flex', alignItems: 'center',
    padding: '0 16px', borderBottom: '1px solid #f0f4f1'
  },
  lbHeaderRow: {
    background: '#0e1f13', padding: '10px 16px',
    fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)',
    fontFamily: "'Barlow Condensed', sans-serif",
    borderBottom: 'none'
  },
  lbColRank: { width: 44, flexShrink: 0, textAlign: 'center', padding: '12px 0' },
  lbColPlayer: { width: 140, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' },
  lbColRound: {
    width: 44, flexShrink: 0, textAlign: 'center', padding: '10px 0',
    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  lbColTotal: { width: 64, flexShrink: 0, textAlign: 'right', padding: '12px 0' },
  lbRankNum: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 14, color: '#6b7c6e' },
  lbAvatar: {
    width: 32, height: 32, borderRadius: '50%', fontSize: 16, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  lbName: { fontWeight: 700, fontSize: 12, color: '#0e1f13', lineHeight: 1.2 },
  lbGuestTag: { fontSize: 9, color: '#9aaa9d', fontStyle: 'italic' },
  lbHcp: { fontSize: 9, color: '#9aaa9d' },
  lbTotalNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: '#0e1f13', textAlign: 'right', lineHeight: 1
  },

  // ── Schedule section ──
  scheduleSection: { padding: '0 0 16px' },
  scheduleSectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 13, color: '#0e1f13', letterSpacing: 2,
    padding: '16px 16px 10px', background: 'white',
    borderBottom: '1px solid #f0f4f1', marginBottom: 2
  },
  scheduleCard: {
    background: 'white', marginBottom: 2,
    borderBottom: '1px solid #f0f4f1',
    display: 'flex', flexDirection: 'column'
  },
  scheduleRoundRow: {
    padding: '14px 16px',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12
  },
  scheduleLeft: { display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1 },
  scheduleRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 },
  scheduleRoundBadge: {
    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 13, letterSpacing: 0.5
  },
  scheduleName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13', marginBottom: 3
  },
  scheduleDateTime: { fontSize: 11, color: '#6b7c6e', marginBottom: 6 },
  schedulePoints: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  schedulePointsPill: {
    background: '#f4f7f5', border: '1px solid #e2ebe4', borderRadius: 20,
    padding: '2px 8px', fontSize: 9, fontWeight: 700, color: '#6b7c6e'
  },
  scheduleStatusBadge: {
    borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700
  },
  enterResultsBtn: {
    background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  selfReportedTag: {
    fontSize: 9, color: '#1a7a3c', fontWeight: 700, letterSpacing: 0.3, marginTop: 1
  },
  finishToggle: {
    display: 'flex', alignItems: 'center', fontSize: 13,
    fontWeight: 600, color: '#0e1f13', cursor: 'pointer'
  },
  finishBtn: {
    width: '100%', background: 'none', border: '1.5px solid #e2ebe4',
    borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700,
    color: '#6b7c6e', cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },

  // ── Matchups ──
  matchupsList: {
    padding: '0 16px 4px',
    display: 'flex', flexDirection: 'column', gap: 8
  },
  matchupCard: {
    background: '#f4f7f5', borderRadius: 12,
    border: '1px solid #e2ebe4', overflow: 'hidden'
  },
  matchupCardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', background: 'white', borderBottom: '1px solid #f0f4f1'
  },
  matchupName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 14, color: '#0e1f13'
  },
  matchupStatusBadge: {
    borderRadius: 20, padding: '2px 8px', fontSize: 9, fontWeight: 700
  },
  scoreMatchupBtn: {
    background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif"
  },
  deleteMatchupBtn: {
    background: 'none', border: 'none', color: '#9aaa9d',
    fontSize: 12, cursor: 'pointer', padding: '4px 6px'
  },
  matchupEntries: {
    display: 'flex', flexDirection: 'column', gap: 0
  },
  matchupEntryRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', borderBottom: '1px solid #eef2ef'
  },
  matchupEntryRank: {
    width: 24, flexShrink: 0, textAlign: 'center', fontSize: 14
  },
  matchupEntryRankNum: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 13, color: '#6b7c6e'
  },
  matchupEntryAvatar: {
    width: 26, height: 26, borderRadius: '50%', fontSize: 14, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  matchupEntryName: {
    fontWeight: 700, fontSize: 12, color: '#0e1f13', flex: 1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  matchupEntryHole: {
    fontSize: 9, fontWeight: 800, color: '#2aad56', letterSpacing: 0.3
  },
  matchupEntryScore: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13'
  },
  matchupEntryPts: {
    background: '#e8f5ed', color: '#1a7a3c', borderRadius: 20,
    padding: '2px 7px', fontSize: 9, fontWeight: 800
  },
  addMatchupBtn: {
    margin: '8px 16px 12px',
    background: 'none', border: '1.5px dashed #c8d5ca',
    borderRadius: 10, padding: '9px 0', width: 'calc(100% - 32px)',
    fontSize: 12, fontWeight: 700, color: '#6b7c6e', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif'", letterSpacing: 0.3
  },

  // ── Modal overlay ──
  modalOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  modal: {
    background: 'white',
    borderRadius: 20,
    width: '100%', maxWidth: 520,
    maxHeight: '80vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 16px 12px', borderBottom: '1px solid #f0f4f1', flexShrink: 0
  },
  modalTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 16, color: '#0e1f13'
  },
  modalCloseBtn: {
    background: '#f4f7f5', border: 'none', color: '#6b7c6e',
    width: 30, height: 30, borderRadius: '50%', fontSize: 13,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  // ── Enter results ──
  pointsReminder: {
    display: 'flex', background: '#0e1f13', padding: '12px 16px', gap: 0, flexShrink: 0
  },
  pointsReminderCell: { flex: 1, textAlign: 'center' },
  pointsReminderNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 24, color: '#c8f135', lineHeight: 1
  },
  pointsReminderLabel: {
    fontSize: 9, color: 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase', letterSpacing: 1, marginTop: 3
  },
  resultsBody: { background: 'white' },
  resultsHeaderRow: {
    display: 'flex', alignItems: 'center', padding: '10px 16px',
    borderBottom: '1px solid #f0f4f1', gap: 8, flexShrink: 0
  },
  resultsColLabel: {
    width: 60, flexShrink: 0, textAlign: 'center',
    fontSize: 9, fontWeight: 700, color: '#9aaa9d',
    letterSpacing: 1, textTransform: 'uppercase'
  },
  resultRow: {
    display: 'flex', alignItems: 'center', padding: '10px 16px',
    borderBottom: '1px solid #f0f4f1', gap: 8
  },
  resultPlayerCell: { flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  resultAvatar: {
    width: 34, height: 34, borderRadius: '50%', fontSize: 18, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  resultName: { fontWeight: 700, fontSize: 13, color: '#0e1f13', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  resultGuestTag: { fontSize: 9, color: '#9aaa9d', fontStyle: 'italic' },
  resultInputCell: { width: 60, flexShrink: 0 },
  resultPosInput: {
    width: '100%', background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 8, padding: '8px 4px', fontSize: 16, fontWeight: 900,
    color: '#0e1f13', outline: 'none', textAlign: 'center',
    fontFamily: "'Barlow Condensed', sans-serif", boxSizing: 'border-box'
  },
  resultScoreInput: {
    width: '100%', background: '#f4f7f5', border: '1px solid #e2ebe4',
    borderRadius: 8, padding: '8px 4px', fontSize: 14,
    color: '#0e1f13', outline: 'none', textAlign: 'center',
    fontFamily: "'Barlow Condensed', sans-serif", boxSizing: 'border-box'
  },
  resultPtsCell: { width: 60, flexShrink: 0, textAlign: 'center' },
  resultPtsBadge: {
    borderRadius: 8, padding: '6px 0',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 16, display: 'inline-block', minWidth: 46, textAlign: 'center'
  },

  // ── Format selectors ──
  formatCard: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 14, padding: '14px 16px', cursor: 'pointer'
  },
  formatCardActive: {
    border: '2px solid #1a7a3c', background: '#f7fdf9'
  },
  formatCardTitle: {
    fontWeight: 700, fontSize: 14, color: '#0e1f13', marginBottom: 3,
    fontFamily: "'Barlow', sans-serif"
  },
  formatCardDesc: {
    fontSize: 12, color: '#6b7c6e', lineHeight: 1.4
  },
  formatDot: {
    width: 10, height: 10, borderRadius: '50%', background: '#1a7a3c', flexShrink: 0
  },
  formatPill: {
    background: '#e8f5ed', color: '#1a7a3c', borderRadius: 20,
    padding: '4px 12px', fontSize: 11, fontWeight: 700,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.3
  }
}
